import React, { createContext, ReactNode, useContext, useState } from "react";
import UserManagementService from "Services/UserManagement/UserManagementService";
import { toast } from "react-toastify";

/* =======================
   Types
======================= */
export interface FilterDataI {
  pageSize: number;
  pageNumber: number;
  sortColumn: string;
  sortDirection: string;
  releaseTag: string;
  activeTab: string; 
}

export interface SortI {
  sortingOrder: string;
  clickedIconData: string;
}

export interface SearchRequestI {
  search: string;
  requirement: string;
  ticketNumber: string;
  category: string;
  priorityName: string;
  dateSubmitted: string;
  currentTaskStatus: string;
  qa: string;
  deploymentDate: string;
  releaseTag: string;
  taskComments: string;
  reportedBy: string;
}

export interface TaskTrackerTagI {
  name: string;
  tagBg?: string;
  tagFg?: string;
}

export interface TaskTrackerRowI {
  id: string;
  name: string;
  description: string;
  requirement: string;
  ticketNumber: string | null;
  category: string | null;
  priority: string | null;
  priorityName: string | null;
  dateSubmitted: string | null;
  currentTaskStatus: string | null;
  statusColor: string | null;
  deploymentDate: string | null;
  releaseTag: string | null;
  portal: string | null;
  moduleName: string | null;
  podName: string | null;
  lab: string | null;
  ba: string | null;
  qa: string | null;
  productOwner: string | null;
  taskComments: string | null;
  reportedBy: string | null;
  creatorName: string | null;
  creator?: {
    username: string;
    email: string;
  };

 tags?: TaskTrackerTagI[];
  customFields?: any[];
}

type Context = {
  rows: TaskTrackerRowI[];
  setRows: React.Dispatch<React.SetStateAction<TaskTrackerRowI[]>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  total: number;
  setTotal: React.Dispatch<React.SetStateAction<number>>;
  openCount: number;
  setOpenCount: React.Dispatch<React.SetStateAction<number>>;
  closeCount: number;
  setCloseCount: React.Dispatch<React.SetStateAction<number>>;
  filterData: FilterDataI;
  setFilterData: React.Dispatch<React.SetStateAction<FilterDataI>>;
  searchRequest: SearchRequestI;
  setSearchRequest: React.Dispatch<React.SetStateAction<SearchRequestI>>;
  sort: SortI;
  setSort: React.Dispatch<React.SetStateAction<SortI>>;
  loadGridData: (shouldPageLoad?: boolean) => Promise<any>;
  resetSearch: () => void;
  refreshGrid: () => Promise<void>;
  releaseColors: Record<string, string>;
};

export const initialFilterData: FilterDataI = {
  pageSize: 50,
  pageNumber: 1,
  sortColumn: "",
  sortDirection: "",
  releaseTag: "",
  activeTab: "open", 
};

export const initialSearchRequest: SearchRequestI = {
  search: "",
  requirement: "",
  ticketNumber: "",
  category: "",
  priorityName: "",
  dateSubmitted: "",
  currentTaskStatus: "",
  qa: "",
  deploymentDate: "",
  releaseTag: "",
  taskComments: "",
  reportedBy: "",
};

const context = createContext<Context>({} as Context);

export default function TaskTrackerContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [rows, setRows] = useState<TaskTrackerRowI[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);
  const [openCount, setOpenCount] = useState<number>(0);
  const [closeCount, setCloseCount] = useState<number>(0);
  const [filterData, setFilterData] = useState<FilterDataI>(initialFilterData);
  const [searchRequest, setSearchRequest] = useState<SearchRequestI>(initialSearchRequest);
  const [sort, setSort] = useState<SortI>({
    sortingOrder: "",
    clickedIconData: "",
  });

  const [releaseColors, setReleaseColors] = useState<Record<string, string>>({});
 

  const loadGridData = async (shouldPageLoad = true): Promise<any> => {
    setLoading(shouldPageLoad);

    const sortColumn = filterData.sortColumn ? String(filterData.sortColumn).trim() : "";
    const sortDirection = filterData.sortDirection ? String(filterData.sortDirection).trim() : "";

    const payload = {
      pageNumber: filterData.pageNumber || 1,
      pageSize: filterData.pageSize || 50,
      sortColumn: sortColumn,
      sortDirection: sortDirection,
      queryModel: {
        Search: searchRequest.search?.trim() || "",
        Tab: filterData.activeTab || "open", // Sends "open" or "closed"
        IssuesRequirement: searchRequest.requirement?.trim() || "",
        TicketNumber: searchRequest.ticketNumber?.trim() || "",
        Category: searchRequest.category?.trim() || "",
        Priority: searchRequest.priorityName?.trim() || "",
  Status: searchRequest.currentTaskStatus?.trim() || "",
          DateSubmitted: searchRequest.dateSubmitted?.trim() || "",
  DeploymentDate: searchRequest.deploymentDate?.trim() || "",
  TaskComments: searchRequest.taskComments?.trim() || "",
        ReportedBy: searchRequest.reportedBy?.trim() || "",
        ReleaseTag: filterData.releaseTag === "AllReleases" ? "" : filterData.releaseTag || "",
        Refresh: false, 
      },
    };

    try {
      const response: any = await UserManagementService.ClickUpApi(payload);

      if (response?.data?.statusCode === 200 || response?.status === 200) {
        setRows(response?.data?.data ?? []);
        setTotal(response?.data?.total ?? 0);
         if (response?.data?.releaseColors) {
    setReleaseColors(response.data.releaseColors);
  }
        if (response?.data?.openCount !== undefined) {
          setOpenCount(response.data.openCount);
        }
        if (response?.data?.closeCount !== undefined) {
          setCloseCount(response.data.closeCount);
        }
      } else {
        setRows([]);
        setTotal(0);
      }
      return response;
    } catch (error) {
      console.error("Error loading task tracker data:", error);
      setRows([]);
      setTotal(0);
      return error;
    } finally {
      setLoading(false);
    }
  };

  const resetSearch = () => {
    setSearchRequest(initialSearchRequest);
    setFilterData(initialFilterData);
    setSort({ sortingOrder: "", clickedIconData: "" });
    setTimeout(() => loadGridData(true), 0);
  };


  const emptyQueryModel = {
  Search: "",
  // Tab:"",
  Tab: filterData.activeTab || "open",
  IssuesRequirement: "",
  TicketNumber: "",
  Category: "",
  Priority: "",
  Status: "",
  ReportedBy: "",
  ReleaseTag: "",
  Refresh: false,
};

const refreshGrid = async () => {
 try{ 
   setLoading(true);
  const payload = {
    pageNumber: 1,
    pageSize: filterData.pageSize,
    sortColumn: filterData.sortColumn,
    sortDirection: filterData.sortDirection,
    queryModel: {
      ...emptyQueryModel,
      Refresh: true, // only this one is set
    },
  };

 const response: any = await UserManagementService.ClickUpApi(payload);

    if (response?.data?.statusCode === 200 || response?.status === 200) {
      setRows(response?.data?.data ?? []);
      setTotal(response?.data?.total ?? 0);
       if (response?.data?.releaseColors) {
    setReleaseColors(response.data.releaseColors);
  }
      if (response?.data?.openCount !== undefined) setOpenCount(response.data.openCount);
      if (response?.data?.closeCount !== undefined) setCloseCount(response.data.closeCount);

      
      
      // Show toast only when the backend sends a message
      if(response?.data?.refreshRemaining!==null) {


        const refreshMessage = response?.data?.refreshMessage;
        if (refreshMessage) {
          toast.warning(refreshMessage);
        }
      } 
     
    }
  } catch (error: any) {
    console.error("Error refreshing task tracker data:", error);

    if(error?.data?.refreshRemaining !== null){
    const errorMessage = error?.response?.data?.refreshMessage
    if (errorMessage) {
      toast.warning(errorMessage);
    }}

  } finally {
    setLoading(false);
  }
};

  return (
    <context.Provider
      value={{
        rows,
        setRows,
        loading,
        setLoading,
        total,
        setTotal,
        openCount,
        setOpenCount,
        closeCount,
        setCloseCount,
        filterData,
        setFilterData,
        searchRequest,
        setSearchRequest,
        sort,
        setSort,
        loadGridData,
        resetSearch,
        refreshGrid,
        releaseColors
        
      }}
    >
      {children}
    </context.Provider>
  );
}

export const useTaskTrackerContext = () => useContext(context);
